//@ts-check
import { Controller } from "@hotwired/stimulus";
import { getCsrfToken } from "common/utils/rails";

/**
 * Handles changes to licensing options on the cart page.
 */
export default class extends Controller {
  connect() {
    this.listenToLicensingOptionsChange();
  }

  // --- Element queries ---
  get licensingOptionsGroupSelects() {
    // Rendered in app/views/store/licensing/_selector_options.html.erb
    return this.element.querySelectorAll(
      'select[name="licensing_option_ids[]"]'
    );
  }

  // Radio inputs rendered by Licensing::SelectorOptionsComponent when a group uses display_as="radio".
  get licensingOptionsGroupRadios() {
    return this.element.querySelectorAll(
      'input[type="radio"][data-licensing-option-radio]'
    );
  }

  get cartSectionElement() {
    return this.element
      .closest(".shopping-cart-page")
      .querySelector(".cart-section");
  }

  listenToLicensingOptionsChange() {
    this.licensingOptionsGroupSelects.forEach((select) => {
      select.addEventListener("change", (event) => {
        if (!(event.target instanceof HTMLSelectElement)) {
          console.error(
            "Cannot handle licensing options change because the event target is not an HTMLSelectElement"
          );
          return;
        }
        this.saveLicensingOptionsFromSelection();
      });
    });
    this.licensingOptionsGroupRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.saveLicensingOptionsFromSelection();
      });
    });
  }

  async saveLicensingOptionsFromSelection() {
    const payload = this.getPayloadForServer();
    await this.saveToServer(payload);
  }

  /**
   * Collects the selected licensing options for each medium and prepares the payload
   * to be sent to the server.
   *
   * @returns {Object} Payload in the format:
   *     {
   *       "medium_id_1": ["option_id_1", "option_id_2"],
   *       "medium_id_2": ["option_id_3"],
   *     }
   */
  getPayloadForServer() {
    const payload = {};

    this.element
      .querySelectorAll(".shopping-cart-licensing__medium")
      .forEach((element) => {
        const mediumRow = mustBeHtmlElement(element, "licensing medium row");

        const mediumId = mustBePresent(
          mediumRow.dataset.licensingMediumId,
          "data-licensing-medium-id"
        );
        const selectedOptions =
          this.getSelectedOptionIdsForMediumRow(mediumRow);

        if (selectedOptions.length > 0) {
          payload[mediumId] = selectedOptions;
        }
      });

    return payload;
  }

  /**
   * @param {HTMLElement} mediumRow
   */
  getSelectedOptionIdsForMediumRow(mediumRow) {
    const optionSelects = mediumRow.querySelectorAll(
      'select[name="licensing_option_ids[]"]'
    );
    const selectValues = Array.from(optionSelects)
      .map((select) => mustBeHtmlSelectElement(select, "option select").value)
      .filter((value) => value);

    // Collect the currently checked radio per group (for groups rendered as radios).
    const checkedRadios = mediumRow.querySelectorAll(
      'input[type="radio"][data-licensing-option-radio]:checked'
    );
    const radioValues = Array.from(checkedRadios)
      .map((radio) => mustBe(HTMLInputElement, radio, "option radio").value)
      .filter((value) => value);

    return [...selectValues, ...radioValues];
  }

  async saveToServer(payload) {
    console.log("Saving licensing options to server:", payload);

    const response = await fetch("/cart/select_licensing_options", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({ licensing_options_data: payload }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to save licensing options to server: ${response.statusText}`
      );
    }

    // Server returns HTML
    const html = await response.text();

    // --- Handle response from server ---

    // html from shopping_cart/_cart.html.erb
    this.cartSectionElement.outerHTML = html;
  }
}

function mustBe(klass, value, name = "value") {
  mustBePresent(value, name);
  if (!(value instanceof klass)) {
    console.log("value:", value);
    throw new Error(
      `Expected "${name}" to be an instance of ${klass.name}, but got: ${value}`
    );
  }
  return value;
}

const mustBeHtmlElement = mustBe.bind(null, HTMLElement);
const mustBeHtmlSelectElement = mustBe.bind(null, HTMLSelectElement);

function mustBePresent(value, name = "value") {
  if (value === undefined || value === null || value === "") {
    throw new Error(`"${name}" must be present`);
  }
  return value;
}
